import Phaser from "phaser";
import type { BrickCellKind } from "../data/ChapterData.js";
import { PALETTE_NUM } from "../themes/palette.js";
import { resolveBrickSparkleParticles } from "./brickSparkle.js";

const SPARKLE_REDRAW_INTERVAL_MS = 66;
const SPARKLE_MIN_DRAW_ALPHA = 0.05;

export interface BrickSparkleSpec {
  readonly kind: BrickCellKind;
  readonly isCircuitVisual: boolean;
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  readonly sparklePhase: number;
  readonly timeMs: number;
  readonly lastDrawMs: number;
  readonly sparkle: Phaser.GameObjects.Graphics;
}

export function drawBrickSparkle(spec: BrickSparkleSpec): number {
  if (spec.kind !== "mystery" || spec.isCircuitVisual) {
    spec.sparkle.clear().setActive(false).setVisible(false);
    return spec.lastDrawMs;
  }
  spec.sparkle.setPosition(spec.x, spec.y);
  if (spec.timeMs - spec.lastDrawMs < SPARKLE_REDRAW_INTERVAL_MS) return spec.lastDrawMs;

  spec.sparkle.clear();
  const particles = resolveBrickSparkleParticles(
    spec.kind,
    spec.displayWidth,
    spec.displayHeight,
    spec.sparklePhase,
    spec.timeMs,
  );
  const colors = [PALETTE_NUM.accentAmber, PALETTE_NUM.textBright, PALETTE_NUM.accentGold] as const;
  for (const p of particles) {
    if (p.alpha < SPARKLE_MIN_DRAW_ALPHA) continue;
    const color = colors[p.colorSlot];
    spec.sparkle
      .lineStyle(Math.max(1, p.radius * 0.45), color, p.alpha * 0.08)
      .beginPath()
      .moveTo(p.x + p.trailX, p.y + p.trailY)
      .lineTo(p.x, p.y)
      .strokePath()
      .lineStyle(Math.max(1, p.radius * 0.32), color, p.alpha * 0.18)
      .beginPath()
      .moveTo(p.x - p.ray, p.y)
      .lineTo(p.x + p.ray, p.y)
      .moveTo(p.x, p.y - p.ray)
      .lineTo(p.x, p.y + p.ray)
      .strokePath()
      .fillStyle(color, p.alpha * 0.14)
      .fillCircle(p.x, p.y, p.radius)
      .fillStyle(PALETTE_NUM.textBright, p.alpha * 0.08)
      .fillCircle(p.x, p.y, Math.max(1, p.radius * 0.38));
  }
  spec.sparkle.setActive(true).setVisible(true);
  return spec.timeMs;
}
